import { useEffect, useMemo, useState, useCallback } from 'react'
import {
  correctableFromCase,
  subscribeRecoveryMessages,
  subscribeRecoveryNudge,
  getRecoveryCardImage,
  markRecoveryHandled,
  returnRecoveryToFlow,
  sendRecoverySms,
  type RecoveryCase,
  type RecoveryMessage,
  type RecoveryNudge,
} from '../services/recoveryCases'
import { recoveryBandFor, nudgeExplainer } from '../utils/recoveryBand'
import { logPhiView } from '../services/audit'
import { formatPhone, formatIsoDateShort } from '../utils/formatters'
import type { Clinic, EligibilityCheckDoc } from '../types'
import * as Sentry from '@sentry/react'
import { resolveChainHead } from '../services/eligibilityChecks'
import { ExtractionPane, ConversationThread } from './SmsThreadView'
import RecoveryStatusBand from './RecoveryStatusBand'
import { useCardImageUrls, caseCardUrl, caseCardImages, fmtDob } from '../utils/smsThread'
import { UNSAVED_WARNING } from '../utils/unsavedWork'
import { useUnsavedGuard } from '../utils/useUnsavedGuard'
import { useBeforeUnloadGuard } from '../utils/useBeforeUnloadGuard'
import { useSlideout } from '../hooks/useSlideout'
import { canCorrect } from '../utils/checkCorrection'
import { parseFlexibleDate } from '../utils/parseFlexibleDate'
import { cardReadSummary, buildCardDetailRows } from '../utils/cardDetailRows'
import { useCardCorrection } from '../hooks/useCardCorrection'
import { recoveryCheckTarget } from '../../shared/recoveryDrawer'

interface RecoveryLoopSlideoutProps {
  case: RecoveryCase
  orgId: string
  isStaff: boolean
  /** False for a view-mode staff impersonation: the card rows read as a list
   *  of facts and Save / Re-check stay disabled. The callable would refuse the
   *  write anyway (`requireWrite`), but only after someone had typed into PHI
   *  fields — the same gap the check panels closed with this flag. */
  canWrite?: boolean
  /** The case's clinic — supplies the timezone + quiet-hours window the nudge
   *  copy needs. Absent for a case whose clinic we can't resolve; the copy then
   *  omits the send time rather than quoting the reader's local clock. */
  clinic?: Clinic | null
  onClose: () => void
  /** Called after a successful Mark handled — closes the drawer; the live
   *  list picks up the state change on its own. */
  onHandled: () => void
  /** Tab shown on open — 'conversation' when arriving via the check slideout's
   *  "View SMS conversation" link. Applied on mount (key the component per case). */
  initialTab?: 'extraction' | 'conversation'
  /** Open an eligibility check's slideout — `checkId` is the link's target
   *  (#279): the fired re-run when one exists, else the originating check. */
  onOpenCheck?: (checkId: string) => void
  /**
   * Where the drawer publishes its browser-Back DECISION (#349 A11). The host
   * owns the history sentinel — it has to live in a component that stays
   * mounted across the close, or the slot is stranded (see RecoveryLoopList) —
   * and calls this on a Back press: `'refused'` keeps the drawer open over
   * typed work and re-arms the entry; anything else means the drawer closed.
   */
  backHandlerRef?: { current: (() => void | 'refused') | null }
}

/** "9 PM" / "7 AM" from a 0–23 clinic-local hour — the patient's time, not the
 *  reader's, so the after-hours confirm names the clock that actually matters. */
function formatLocalHour(hour: number): string {
  const h = ((hour % 12) + 12) % 12 || 12
  return `${h} ${hour < 12 ? 'AM' : 'PM'}`
}

export default function RecoveryLoopSlideout({
  case: rc, orgId, isStaff, canWrite = true, clinic, onClose, onHandled, initialTab, onOpenCheck,
  backHandlerRef,
}: RecoveryLoopSlideoutProps) {
  // #274 — one component, four surfaces, one behaviour: closing this panel
  // mid-correction warns before it discards.
  // `guardAction` covers the paths that NAVIGATE instead of closing — "View
  // check →" / "Original failed check" (the list view unmounts this drawer)
  // and "Mark handled" (onHandled() deselects). All three sat in the same tab
  // as the editor and went straight past a half-typed correction (#318 review).
  const { guardedClose, guardAction, attemptClose, setDirty, dirty, showWarning } =
    useUnsavedGuard(onClose)
  const [tabState, setTab] = useState<'extraction' | 'conversation'>(initialTab ?? 'extraction')
  const [messages, setMessages] = useState<RecoveryMessage[]>([])
  const [handling, setHandling] = useState(false)
  const [messaging, setMessaging] = useState(false)
  const [composing, setComposing] = useState(false)
  const [draft, setDraft] = useState('')
  // Escape closes this panel like every other slideout — through the guard.
  // Found on the #318 final pass: the drawer never listened for Escape at all,
  // the one surface out of four where that close path didn't exist.
  const panelRef = useSlideout(guardedClose)
  // #349: the card-row correction, owned HERE rather than inside the Extraction
  // pane — the pane unmounts on a tab switch, and a draft that lives in it is
  // lost by switching to Conversation and back. One controller per case, shared
  // by the pane's rows and the full-screen viewer: one dirty flag, one save.
  /**
   * The NEWEST check in this case's correction chain (#349).
   *
   * The case is named after the first failed check and its id can never move,
   * so the drawer is always handed that original — which is why a corrected
   * case kept showing pre-correction values. The head is read by the case's
   * FLOW key — every check in a chain carries it, whichever path minted it —
   * with the `supersededBy` walk as the fallback for pre-flow docs (see
   * `resolveChainHead`). Resolved when the drawer opens and again the moment a
   * correction fires — from the check it created, which IS the new head.
   *
   * Not a subscription: the callable names the check it just created, so one
   * read fetches everything the rows need; across sessions a reopen
   * re-resolves. A live query per open drawer would buy only "watch it change
   * while staring at it".
   */
  const [latestInFlow, setLatestInFlow] = useState<EligibilityCheckDoc | null>(null)
  /**
   * The check a correction just created, straight from the callable's result.
   *
   * The outcome names the new check synchronously, so the edit target moves the
   * moment it lands — until the target moves the controller stays latched as
   * "already saved", which left the button permanently disabled for the next
   * correction. The head is then re-resolved FROM it (the effect below), so
   * the rows' seed catches up one read later. Without that read the draft kept
   * comparing against the pre-correction values, and a committed edit went on
   * reading as unsaved: amber dot, an enabled "Save & re-check", the
   * unsaved-edits bar on every close path, and no way to reach "Mark handled".
   */
  const [firedCheckId, setFiredCheckId] = useState<string | null>(null)
  const headFrom = firedCheckId ?? rc.checkId
  /**
   * Which id the head read has SETTLED for — success or failure. Until it has,
   * the edit target below is still the case's frozen original, and a press
   * then would re-fire a check that may already be superseded: `retirePriorCheck`
   * rewrites its `supersededBy`, leaving two live successors of one visit.
   * The controller is told it is not `ready` for that window — the rows stay
   * read-only and nothing can fire — which also holds the one read after a
   * correction fires, and for good on a read that failed.
   */
  const [headSettledFor, setHeadSettledFor] = useState<string | null>(null)
  /** The head read FAILED (permission, offline) — the footer says to reopen. */
  const [headError, setHeadError] = useState(false)
  useEffect(() => {
    let live = true
    resolveChainHead(orgId, headFrom, { flowId: rc.flowId, clinicId: rc.clinicId })
      // A MISSING starting check (head null) still settles: there is nothing
      // newer to find, and the case's own check is the honest fallback.
      .then((head) => {
        if (!live) return
        setLatestInFlow(head)
        setHeadError(false)
        setHeadSettledFor(headFrom)
      })
      // A read that FAILS settles nothing. The walk may have stopped short of
      // the head, so the only target on offer is a check that may already be
      // superseded — and firing from it is the fork this gate exists to
      // prevent. Save and Re-check stay inert; reopening the drawer re-runs
      // the read, so the operator is never locked out for good.
      .catch((err) => {
        if (!live) return
        Sentry.captureException(err)
        setHeadError(true)
      })
    return () => { live = false }
  }, [orgId, headFrom, rc.flowId, rc.clinicId])
  const chainResolved = headSettledFor === headFrom

  /**
   * What the check ASKED the payer — the rows' value and the editor's seed
   * (#349). Not the OCR read: the pane is one surface now, so a row holds one
   * value, and the question being answered is "is this what we should be
   * asking?". The card read stays on screen as the evidence beside it.
   *
   * Taken from the newest check in the flow, falling back to the case's own
   * check until that subscription reports (and for a case with no flow key).
   */
  const asked = useMemo(() => {
    const sub = latestInFlow?.requestSubscriber
    const id = latestInFlow?.identity as Record<string, string> | undefined
    const pick = (...vals: Array<string | null | undefined>) => {
      for (const v of vals) { const t = (v ?? '').trim(); if (t) return t }
      return ''
    }
    // The stored date is whatever the source said: checks minted before the
    // ingest normalized dates carry "05/17/1988", and the native date control
    // renders only ISO — anything else shows EMPTY, which is the #322 failure
    // (the date-of-birth case opening with the date of birth blank). Same
    // normalization `draftFromCheck` applies; an unparseable date stays blank
    // in the control and shows as-is on the resting row.
    const iso = (stored: string) => (stored ? parseFlexibleDate(stored) ?? '' : '')
    return latestInFlow
      ? {
        firstName: pick(sub?.firstName, id?.firstName, rc.submitted.firstName),
        lastName: pick(sub?.lastName, id?.lastName, rc.submitted.lastName),
        dateOfBirth: iso(pick(sub?.dateOfBirth, id?.dob, rc.submitted.dateOfBirth)),
        memberId: pick(sub?.memberId, id?.memberId, id?.policyNumber, rc.submitted.memberId),
        payerName: pick(latestInFlow.payerName, rc.payerName),
      }
      : {
        firstName: rc.submitted.firstName,
        lastName: rc.submitted.lastName,
        dateOfBirth: iso(rc.submitted.dateOfBirth),
        memberId: rc.submitted.memberId,
        payerName: rc.payerName,
      }
  }, [latestInFlow, rc.submitted, rc.payerName])
  // What the read achieved, for the actions the footer may honestly offer
  // (#349 A7). Null when no card has been read at all: "send a clearer photo"
  // to someone who has sent nothing reads as though nobody looked.
  // Built WITHOUT `asked` on purpose: this summary answers "what did the photo
  // give us", which is what decides whether a clearer photo would help. Folding
  // the asked values in made a field the card could not read look readable the
  // moment the check held any value for it, and the clearer-photo offer
  // disappeared exactly where it was needed.
  const cardRead = useMemo(
    () => (rc.extraction
      ? cardReadSummary(buildCardDetailRows({
        extraction: rc.extraction,
        recordDob: rc.dob,
        recordPayerName: rc.payerName,
        statedFields: rc.statedFields,
      }))
      : null),
    [rc.extraction, rc.dob, rc.payerName, rc.statedFields],
  )
  const askForClearerPhoto = !!cardRead && (cardRead.anyUnreadable || cardRead.nothingReadable)
  /**
   * The check a correction is fired FROM — the newest in the flow, not the
   * case's frozen original (#349).
   *
   * Correcting the original a second time would mint a second successor off one
   * source: two live answers for one visit, both counted. Following the flow
   * keeps the chain linear, and means a second correction builds on the first.
   *
   * Once the head has been read it wins over the id of the check a correction
   * just created: the read starts FROM that check, so if anything replaced it
   * in the meantime the head is newer still. Before the read settles the fired
   * id stands in — it is what moves the controller's target and releases its
   * saved latch — and nothing can fire in that window anyway.
   */
  const targetCheckId = chainResolved
    ? (latestInFlow?.id ?? firedCheckId ?? rc.checkId)
    : (firedCheckId ?? rc.checkId)
  const editTarget = useMemo(() => ({
    ...correctableFromCase(rc),
    id: targetCheckId,
    ...(latestInFlow && latestInFlow.id === targetCheckId
      ? {
        status: latestInFlow.status,
        payerName: latestInFlow.payerName ?? rc.payerName,
      }
      // A check we know exists but have not read yet is not pending — treating
      // it as such would hide the editor for the one read the re-resolve takes.
      : targetCheckId !== rc.checkId
        ? { status: 'error' as const }
        : {}),
  }), [rc, latestInFlow, targetCheckId])
  const canEditCard = canCorrect(editTarget)
  const cardCorrection = useCardCorrection(canEditCard ? {
    orgId,
    check: editTarget,
    seed: asked,
    canWrite,
    ready: chainResolved,
    // Deliberately does NOT close. The drawer follows the flow, so staying open
    // is how the operator sees the answer to the question they just corrected —
    // closing on success meant the one screen showing the result vanished at
    // the moment it became worth reading.
    onCorrected: (outcome) => setFiredCheckId(outcome.checkId),
  } : null)
  const cardDirty = !!cardCorrection?.dirty
  /**
   * Which single primary the footer carries (#349).
   *
   * Named rather than nested in the JSX because the states are a sequence, not
   * a set of conditions: typed work outranks everything, and the close-out is
   * what is left. An untouched pane is never re-run from here: re-checking is
   * what a typed correction does, and a case with nothing to type is one staff
   * CLOSE — no coverage, resolved by phone, the visit already happened — not
   * one they buy the same rejected answer for again. So "Re-check" and "Mark
   * handled" never share the footer. `none` is a case with nothing left to
   * close: one staff closed by hand — "Done" used to live there, doing exactly
   * what the ✕ does — or one the loop RECOVERED on its own. A resolved case
   * already sits in the Recovered bucket by its state; "Mark handled" there
   * only stamped a time and changed nothing on screen.
   */
  const primaryAction: 'save' | 'handled' | 'none' =
    cardCorrection?.dirty ? 'save'
      : rc.handled || rc.displayState === 'resolved' ? 'none' : 'handled'
  // Typed work is typed work: the card-row correction OR an unsent patient
  // message in the composer. The overlay used to discard a half-typed text
  // silently because only the editor ever reported dirt.
  useEffect(() => {
    setDirty(cardDirty || (composing && draft.trim().length > 0))
  }, [cardDirty, composing, draft, setDirty])
  // #349 A11 — the two close paths the guard never covered. Browser Back is
  // owned by the LIST, not registered here: a history sentinel gives its slot
  // back only when its open flag flips false while the hook is still mounted,
  // and this drawer unmounts on close — registered in here, every ✕ / overlay /
  // Escape / Mark-handled close stranded one entry and the next Back press did
  // nothing (the #231 dead press). The drawer supplies only the DECISION: Back
  // consumes its history entry before the handler runs, so a refusal has to
  // re-arm, which is what the sentinel's 'refused' return is for. The page
  // leaving entirely can only be intercepted by the browser itself.
  useEffect(() => {
    if (!backHandlerRef) return
    backHandlerRef.current = () => (attemptClose() === 'refused' ? 'refused' : undefined)
    return () => { backHandlerRef.current = null }
  }, [backHandlerRef, attemptClose])
  useBeforeUnloadGuard(dirty)
  const [actionError, setActionError] = useState<string | null>(null)
  /** Patient's clinic-local hour when a send landed outside quiet hours (#242) —
   *  non-null means the confirm prompt is showing, not that the send failed. */
  const [afterHours, setAfterHours] = useState<number | null>(null)

  const [nudge, setNudge] = useState<RecoveryNudge | null>(null)
  const inboundCount = messages.filter((m) => m.direction === 'inbound').length

  // A thread exists iff a text was actually attempted (a no-phone case never
  // gets a conversation doc). Keyed on the id, not on loaded messages, so the
  // tab doesn't flicker from disabled to enabled while the thread streams in.
  const hasThread = !!rc.conversationId
  // Texting is off the table once the patient says STOP — from the case state
  // or from the thread flag, whichever we learn first.
  const optedOut = rc.displayState === 'opted_out' || nudge?.optedOut === true
  // Which check the "View check" link opens (#279) — keyed to the recovery
  // ATTEMPT: the fired re-run once one exists (pending or settled), the
  // originating failure only before any re-run. The label names the check so
  // staff can tell at a glance whether they're opening the failure or the
  // result — a green recovery must never silently land on the check it fixed.
  const target = recoveryCheckTarget(rc)
  /**
   * The chain head outranks the case doc's own linkage once it has been read
   * (#349). `recoveryCheckTarget` knows only what the SMS sweep stamped on the
   * case (`rerunCheckId`), so after a staff correction the link kept opening
   * the failure the operator had just replaced — while the drawer had already
   * read the newest check to seed the rows. Only a head that differs from both
   * the case's target and its original counts: a walk that stops at the
   * original (a doc that predates supersession stamping) knows less than the
   * case does, and a read still in flight or failed falls back to the case's
   * own answer. The label is drawn from the head's status, never from the
   * case state, which a staff correction does not change.
   */
  const newest =
    chainResolved && latestInFlow
      && latestInFlow.id !== target.checkId && latestInFlow.id !== rc.checkId
      ? latestInFlow
      : null
  const linkCheckId = newest?.id ?? target.checkId
  const checkLink =
    newest
      ? newest.status === 'pending'
        ? {
          dot: '↻', dotMod: ' rl-checklink__dot--prog',
          title: 'Newest check — in progress…',
          sub: 'Opens the check now running for this case · result expected within a couple of minutes',
        }
        : newest.status === 'success'
          ? {
            dot: '→', dotMod: ' rl-checklink__dot--neutral',
            title: 'Newest eligibility check — answered',
            sub: 'Opens the latest result for this case · the original is below',
          }
          : {
            dot: '✕', dotMod: ' rl-checklink__dot--fail',
            title: 'Newest check — failed',
            sub: "Opens the latest check's own result · the original is below",
          }
      : target.kind === 'rerun_pending'
      ? {
        dot: '↻', dotMod: ' rl-checklink__dot--prog',
        title: 'Re-run in progress…',
        sub: 'Opens the new check · result expected within a couple of minutes',
      }
      : target.kind === 'rerun_settled' && rc.displayState === 'resolved'
        ? {
          dot: '✓', dotMod: '',
          title: 'New eligibility check — recovered',
          sub: 'Opens the successful result this recovery produced',
        }
        : target.kind === 'rerun_settled' && rc.displayState === 'rerun_failed'
          ? {
            dot: '✕', dotMod: ' rl-checklink__dot--fail',
            title: 'Re-run check — failed again',
            sub: "Opens the re-run's own result · the original check is below",
          }
          // Linkage retained but the case left the re-run family (e.g. STOP
          // arrived mid-rerun → opted_out): the fired check's outcome is
          // unknown here, so the label stays neutral rather than guessing —
          // the opened check tells its own truth.
          : target.kind === 'rerun_settled'
          ? {
            dot: '→', dotMod: ' rl-checklink__dot--neutral',
            title: 'Re-run check',
            sub: 'Opens the check this recovery fired · the original is below',
          }
          : {
            dot: '✕', dotMod: ' rl-checklink__dot--fail',
            title: 'Original eligibility check',
            sub: 'Opens the failed check that started this recovery',
          }
  // Belt for a deep-link that asks for a tab this case can't show.
  const tab = hasThread ? tabState : 'extraction'

  // The carrier's own reason for a send that never landed — read off the newest
  // failed outbound. Staff can't act on "it failed"; they can act on "code
  // 30005, the number may be a landline".
  const deliveryErrorCode = messages
    .filter((m) => m.direction === 'outbound' && m.errorCode)
    .map((m) => m.errorCode)
    .pop() ?? null

  // The band's words. Everything it can key on is live: the case's state, the
  // thread's cadence, and the carrier failure. Missing inputs degrade the copy
  // to a shorter true sentence rather than inventing a nudge time.
  const bandInput = {
    state: rc.displayState,
    handled: rc.handled,
    handledAt: rc.handledAt,
    lastOutboundAt: nudge?.lastOutboundAt ?? null,
    nextNudgeAt: nudge?.nextNudgeAt ?? null,
    nudgeCount: nudge?.nudgeCount ?? null,
    errorCode: deliveryErrorCode,
    rerunFailureReason: rc.rerunFailureReason,
    siblingCardArrivedAt: rc.siblingCardArrivedAt,
    unattributedCardAt: rc.unattributedCardAt,
    otherPatientCardAt: rc.otherPatientCardAt,
    timeZone: clinic?.timezone ?? null,
    quietHours: clinic?.smsQuietHours ?? null,
  }
  const band = recoveryBandFor(bandInput)

  // The nudge cadence spelled out — shown in BOTH tabs while the loop is still
  // chasing the patient, because "is the system on this, or is it me?" is the
  // question staff open this drawer to answer (#216).
  const showNudgeNote = !rc.handled &&
    (rc.displayState === 'sms_sent' || rc.displayState === 'awaiting_reply')
  const nudgeNote = showNudgeNote ? nudgeExplainer(bandInput) : null

  // PHI-view beacon (#185): the drawer shows the patient's identity, thread,
  // and card read — an sms_conversation view, not a plain check-result view
  // (its own type also keeps it from deduping against the Dashboard's
  // eligibility_result beacon for the same check). Deduped per resource per
  // session inside logPhiView.
  useEffect(() => {
    logPhiView('sms_conversation', rc.checkId)
  }, [rc.checkId])

  // Live thread — subscribed while the drawer is open, torn down on close.
  // A subscription ERROR (permissions, missing index) must not render as the
  // benign "No messages yet." — staff would read it as "patient never replied".
  const [threadError, setThreadError] = useState(false)
  useEffect(() => {
    if (!rc.conversationId) { setMessages([]); setThreadError(false); return }
    setThreadError(false)
    const unsubscribe = subscribeRecoveryMessages(
      orgId, rc.conversationId, rc.clinicId,
      (msgs) => { setThreadError(false); setMessages(msgs) },
      () => { setMessages([]); setThreadError(true) },
    )
    return unsubscribe
  }, [orgId, rc.conversationId, rc.clinicId])

  // Live nudge cadence for the band. No thread → no cadence, and the band says
  // so rather than implying the automation is still working.
  useEffect(() => {
    if (!rc.conversationId) { setNudge(null); return }
    return subscribeRecoveryNudge(orgId, rc.conversationId, setNudge)
  }, [orgId, rc.conversationId])

  const fetchCardImage = useCallback(
    (media: { intakeId: string; objectIndex: number }) => {
      return getRecoveryCardImage({
        orgId, phone: rc.conversationId ?? '',
        intakeId: media.intakeId, objectIndex: media.objectIndex,
      })
    },
    [orgId, rc.conversationId],
  )
  const cardUrls = useCardImageUrls(messages, fetchCardImage)

  const handleMarkHandled = useCallback(async () => {
    setHandling(true)
    setActionError(null)
    try {
      await markRecoveryHandled(orgId, rc.checkId)
      onHandled()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not mark handled.')
      setHandling(false)
    }
  }, [orgId, rc.checkId, onHandled])

  /**
   * #304: put an escalated case back in the flow — the only way the agent
   * resumes. Nothing un-escalates on a timer, deliberately: a case that came
   * back on its own would put the patient back with the robot they asked to
   * get away from.
   */
  const handleReturnToFlow = useCallback(async () => {
    setHandling(true)
    setActionError(null)
    try {
      await returnRecoveryToFlow(orgId, rc.checkId)
      onHandled()
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Could not return this case to the flow.',
      )
      setHandling(false)
    }
  }, [orgId, rc.checkId, onHandled])

  const handleMessagePatient = useCallback(() => {
    if (!rc.phone) {
      setActionError('No phone on file for this patient.')
      return
    }
    setActionError(null)
    // A fresh draft starts without a pending after-hours confirmation — a
    // leftover one would render "Send anyway" on a message nobody has tried yet.
    setAfterHours(null)
    // #307: the ask is phrased around the INFORMATION, never a side of the
    // card. Payers print the member ID wherever they like, and nothing here
    // records which side we already hold — "the front and back" was a guess we
    // were handing staff as a starting point.
    setDraft(`Hi ${rc.firstName || 'there'}, following up on your insurance card for your upcoming visit — could you send us a clear photo of the side with your member ID and group or plan number? Reply STOP to opt out.`)
    setComposing(true)
  }, [rc.phone, rc.firstName])

  /**
   * #349 A7 — the secondary a pane whose card could not be read should offer.
   *
   * Deliberately different wording from the first ask: the patient DID send a
   * photo, and being asked as though they hadn't reads as nobody looked. It is
   * a prefilled draft rather than a send, so the operator can say why this one
   * was hard to read before it goes out.
   */
  const handleAskClearerPhoto = useCallback(() => {
    if (!rc.phone) {
      setActionError('No phone on file for this patient.')
      return
    }
    setActionError(null)
    setAfterHours(null)
    setDraft(`Hi ${rc.firstName || 'there'}, thanks for sending your insurance card — unfortunately the photo came through too blurry for us to read. Could you send another one in good light, with the side showing your member ID and group or plan number fully in frame? Reply STOP to opt out.`)
    setComposing(true)
  }, [rc.phone, rc.firstName])

  const handleCancelDraft = useCallback(() => {
    setComposing(false)
    setActionError(null)
    setAfterHours(null)
  }, [])

  const handleSendDraft = useCallback(async (confirmAfterHours = false) => {
    const text = draft.trim()
    if (!text) return
    setMessaging(true)
    setActionError(null)
    if (!confirmAfterHours) setAfterHours(null)
    try {
      // The backend resolves the destination phone + clinic from the check —
      // the client never sends a phone number.
      const res = await sendRecoverySms({ orgId, checkId: rc.checkId, text, confirmAfterHours })
      if (!res.sent) {
        // Quiet hours isn't a refusal — it's "do you really mean to text this
        // patient right now?" (#242). Surface the patient's local hour and let
        // staff confirm; the confirmed retry is logged server-side as an
        // override rather than sending silently at 9:40 PM.
        if (res.reason === 'quiet_hours') {
          setAfterHours(res.localHour ?? null)
        } else {
          setActionError(res.reason === 'opted_out'
            ? 'Patient has opted out of texts.'
            : res.reason === 'no_phone'
              ? 'No phone on file for this patient.'
              : 'Message could not be sent.')
        }
      } else {
        setAfterHours(null)
        setComposing(false)
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not send the message.')
    } finally {
      setMessaging(false)
    }
  }, [orgId, rc.checkId, draft])

  const name = rc.lastName || rc.firstName ? `${rc.firstName} ${rc.lastName}`.trim() : '—'
  const illegible = rc.extraction?.status === 'cannot_extract' || rc.displayState === 'cannot_extract'
  const emptyNote = rc.displayState === 'no_phone'
    ? 'No phone number on file — the patient could not be texted.'
    : rc.displayState === 'opted_out'
      ? 'The patient opted out of text messages.'
      : 'Waiting for the patient to send a photo of their insurance card.'

  return (
    <>
      <div className="slideout-overlay" onClick={guardedClose} />
      <aside className="slideout-panel rl-slideout" ref={panelRef} tabIndex={-1}>
        <div className="slideout-panel__header">
          <div className="slideout-panel__header-left rl-slideout__headline">
            <h2 className="slideout-panel__title">{name}</h2>
            {/* No status pill up here anymore — the band below owns the
                status, and saying it twice is the noise the redesign removed. */}
            <div className="rl-slideout__subrow">
              <span className="rl-slideout__sub">
                {rc.payerName || 'Unknown payer'}
                {rc.phone ? ` · ${formatPhone(rc.phone)}` : ' · no phone on file'}
                {rc.dob ? ` · DOB ${fmtDob(rc.dob)}` : ''}
                {rc.appointmentDate ? ` · appt ${formatIsoDateShort(rc.appointmentDate)}` : ''}
              </span>
            </div>
          </div>
          <button className="slideout-panel__close" onClick={guardedClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <RecoveryStatusBand band={band} state={rc.displayState} />

        <div className="rl-slideout__tabs" role="tablist">
          <button
            role="tab"
            aria-selected={tab === 'extraction'}
            className={`rl-slideout__tab${tab === 'extraction' ? ' rl-slideout__tab--active' : ''}`}
            onClick={() => setTab('extraction')}
          >
            Extraction
          </button>
          {/* A patient we never texted has nothing to converse about — the tab
              is a dead end, so it's disabled rather than opening an empty chat
              (PO: "no-phone never opens a chat"). */}
          <button
            role="tab"
            aria-selected={tab === 'conversation'}
            className={`rl-slideout__tab${tab === 'conversation' ? ' rl-slideout__tab--active' : ''}`}
            onClick={() => setTab('conversation')}
            disabled={!hasThread}
            title={hasThread ? undefined : 'No conversation — the patient was never texted'}
          >
            Conversation{inboundCount > 0 ? <span className="rl-slideout__tab-badge">{inboundCount}</span> : null}
          </button>
        </div>

        <div className="rl-slideout__body">
          {/* The nudge cadence, in BOTH tabs. "Is the system chasing this, or is
              the ball in my court?" is the question this drawer exists to
              answer, and it has to be answered wherever staff happen to land. */}
          {nudgeNote && <div className="rl-nudge-note">{nudgeNote}</div>}
          {tab === 'extraction' ? (
            <>
              <ExtractionPane
                cardUrl={caseCardUrl(messages, cardUrls, rc.checkId)}
                cardImages={caseCardImages(messages, cardUrls, rc.checkId)}
                recordDob={rc.dob}
                noCardNote={rc.conversationId ? 'No card image received yet.' : 'No conversation for this case.'}
                extraction={rc.extraction}
                illegible={illegible}
                emptyNote={emptyNote}
                recordPayerName={rc.payerName}
                statedFields={rc.statedFields}
                correction={cardCorrection}
                asked={asked}
              />

              {/* #313's editor used to sit here as its own "What we asked the
                  payer" block. It is gone because #349 moved the capability
                  into the Extracted-details rows above: the same five fields
                  were editable in three places on one pane, and the one staff
                  found first was the payer link — the one with clinic-wide
                  consequences. Correcting a value now happens on the surface
                  where it is read against the card. The editor still mounts on
                  the check panels (failure, VSP, EyeMed), which have no card to
                  read against. */}
              {/* Gated per the prototype: a dead-end link before any card data
                  exists teaches staff the buttons can't be trusted (PO punch list). */}
              {onOpenCheck && rc.extraction && (
                <>
                  <button type="button" className="rl-checklink" onClick={() => guardAction(() => onOpenCheck(linkCheckId))}>
                    <span className={`rl-checklink__dot${checkLink.dotMod}`}>{checkLink.dot}</span>
                    <span className="rl-checklink__body">
                      <span className="rl-checklink__title">{checkLink.title}</span>
                      <span className="rl-checklink__sub">{checkLink.sub}</span>
                    </span>
                    <span className="rl-checklink__arrow">View check →</span>
                  </button>
                  {/* The failure the re-run replaced stays reachable — history,
                      not the headline (#279). */}
                  {linkCheckId !== rc.checkId && (
                    <button
                      type="button"
                      className="rl-checklink rl-checklink--origin"
                      onClick={() => guardAction(() => onOpenCheck(rc.checkId))}
                    >
                      <span className="rl-checklink__dot">✕</span>
                      <span className="rl-checklink__body">
                        <span className="rl-checklink__title">Original failed check</span>
                        <span className="rl-checklink__sub">The check that started this recovery</span>
                      </span>
                      <span className="rl-checklink__arrow">View →</span>
                    </button>
                  )}
                </>
              )}
            </>
          ) : threadError ? (
            <div className="rl-thread">
              <div className="rl-empty-note">
                Couldn't load the conversation. You may not have access to this
                thread — try reopening, or contact an administrator.
              </div>
            </div>
          ) : (
            <ConversationThread
              messages={messages}
              cardUrls={cardUrls}
              footnote={illegible ? "Card couldn't be read clearly · routed to staff review" : null}
              extraction={rc.extraction}
              recordDob={rc.dob}
              recordPayerName={rc.payerName}
              statedFields={rc.statedFields}
              caseId={rc.checkId}
              asked={asked}
              correction={cardCorrection}
            />
          )}
        </div>

        <div className="rl-slideout__footer">
          {/* PANEL-level, not inside the editor block: typed work can also be
              an unsent patient message, and the editor is hidden entirely once
              the case's check goes pending — a refusal whose bar lives in a
              block that isn't mounted is an invisible refusal, the #231 dead
              press (found on the #318 final pass: Escape refused silently). */}
          {showWarning && (
            <div className="slideout-unsaved" role="status">
              <span>{UNSAVED_WARNING}</span>
              <button className="slideout-unsaved__discard" onClick={onClose}>
                Discard
              </button>
            </div>
          )}
          {actionError && <div className="rl-slideout__error">{actionError}</div>}
          {/* The head read failed, so Save / Re-check are inert (see the read
              above). Said out loud: an inert button with no reason is the
              #231 dead press. */}
          {headError && (
            <div className="rl-slideout__error" role="alert">
              Couldn&apos;t load the latest check for this case — close and reopen to retry.
            </div>
          )}
          {/* The correction's own outcomes, beside the button that fired it —
              they used to sit up in the rows, half a pane away from the press.
              A "no new check was needed" verdict is the one an operator is most
              likely to miss, and it is the one that explains why nothing
              appeared to happen. */}
          {cardCorrection?.notice && (
            <div className="rl-slideout__notice" role="status">{cardCorrection.notice}</div>
          )}
          {cardCorrection?.error && (
            <div className="rl-slideout__error" role="alert">{cardCorrection.error}</div>
          )}
          {composing && (
            <div className="rl-compose">
              <textarea
                className="rl-compose__input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={4}
                placeholder="Type a message to the patient"
                autoFocus
                disabled={messaging}
              />
              {afterHours !== null && (
                <div className="rl-compose__confirm">
                  It&apos;s {formatLocalHour(afterHours)} for this patient — outside the
                  hours we normally text. Send anyway?
                </div>
              )}
              <div className="rl-compose__row">
                <button
                  className="rl-btn rl-btn--secondary"
                  onClick={handleCancelDraft}
                  disabled={messaging}
                >
                  Cancel
                </button>
                <button
                  className="rl-btn rl-btn--primary"
                  onClick={() => handleSendDraft(afterHours !== null)}
                  disabled={messaging || !draft.trim()}
                >
                  {messaging ? 'Sending…' : afterHours !== null ? 'Send anyway' : 'Send'}
                </button>
              </div>
            </div>
          )}
          <div className="rl-slideout__actions">
            {/* The secondary action is whatever is actually possible for this
                case — never a button that can't do its job. A patient who
                opted out can't be texted, so the offer is a phone call; a
                patient with no number on file gets no offer at all, and the
                band above tells staff to add one. (The prototype's "Add phone
                number" / "Verify phone number" jump to the patient record —
                that navigation doesn't exist yet, and a button that goes
                nowhere is exactly what teaches staff to distrust the screen.) */}
            {rc.phone && optedOut && (
              <a className="rl-btn rl-btn--secondary" href={`tel:${rc.phone}`}>
                Call patient
              </a>
            )}
            {/* #349 A7: when the card arrived but could not be read, the useful
                secondary is a better photo — not a generic "message patient"
                that leaves the operator to work out what to say about a failure
                they can see on screen. */}
            {rc.phone && !optedOut && (
              <button
                className="rl-btn rl-btn--secondary"
                onClick={askForClearerPhoto ? handleAskClearerPhoto : handleMessagePatient}
                disabled={composing}
              >
                {askForClearerPhoto ? 'Ask for a clearer photo' : 'Message patient'}
              </button>
            )}
            {/* #304: offered only on an escalated case. The automation is
                stopped until someone presses this, so it needs to be visible
                exactly where staff land when they pick the case up. */}
            {rc.displayState === 'escalated' && !rc.handled && (
              <button
                className="rl-btn"
                onClick={handleReturnToFlow}
                disabled={handling}
                title="Let the automated follow-up resume on this case"
              >
                {handling ? 'Saving…' : 'Return to automated flow'}
              </button>
            )}
            {/* ONE primary, whose label is the state of the work (#349).
                A typed correction is the thing to commit; with nothing typed it
                is the close-out. "Done" is deleted: it only ever appeared once
                the work was already finished, did exactly what the ✕ does, and
                competed with the save action for this corner. A handled case
                keeps the ✕ and the overlay. */}
            {primaryAction === 'save' && cardCorrection ? (
              <button
                className="rl-btn rl-btn--primary"
                onClick={() => { void cardCorrection.save() }}
                disabled={!cardCorrection.canWrite || !cardCorrection.ready
                  || cardCorrection.saving || cardCorrection.saved}
              >
                {cardCorrection.saving ? 'Re-checking…' : 'Save & re-check'}
              </button>
            ) : primaryAction === 'handled' ? (
              <button
                className="rl-btn rl-btn--primary"
                onClick={() => guardAction(handleMarkHandled)}
                disabled={handling}
              >
                {handling ? 'Saving…' : 'Mark handled'}
              </button>
            ) : null}
          </div>
          {isStaff && (
            <div className="rl-slideout__staff-note">Viewing as staff · card access audited</div>
          )}
        </div>
      </aside>
    </>
  )
}
